import {
  type TouchEvent,
  type UIEvent,
  useCallback,
  useRef,
  useState,
  type WheelEvent,
} from 'react';

/** How far down the list must be before the header gives way. */
const COLLAPSE_AFTER_PX = 24;
/** How far a finger must pull down, once at the top, to bring it back. */
const PULL_TO_REVEAL_PX = 40;
/** The same for a wheel or trackpad, which reports in its own units. */
const WHEEL_TO_REVEAL_PX = 90;
/** Ignore scroll events while the header is still animating. */
const SETTLE_MS = 350;

/**
 * Hides a header while a list is scrolled down, and brings it back when the
 * user pulls past the top of the list.
 *
 * It is driven by the LIST's own scroll container rather than the window, so
 * it works on pages laid out as a fixed-height column with the list scrolling
 * inside it. Attach the handlers to that container.
 *
 * The header does not come back on any upward scroll, or on merely arriving at
 * the top: the user has to keep going a little past it. That keeps it from
 * flickering back while someone is reading up and down a list, and it is also
 * the only signal available when hiding the header leaves a short list with
 * nothing to scroll at all.
 *
 * Nothing else brings it back — not a new page of results, a tab change or a
 * search — so it doesn't jump back in while someone pages through a list.
 * The one exception is `toggle`, for a button: the pull is easy to miss, and
 * a tap is the quick way in either direction.
 * @param enabled - When false the header is always shown.
 * @returns The collapsed flag, the handlers to wire up, and `toggle`.
 */
export function useCollapseOnScroll(enabled: boolean) {
  const [collapsed, setCollapsed] = useState(false);
  const lastTop = useRef(0);
  const settledAt = useRef(0);
  // Where the finger was when the list was last NOT at the top, so the pull
  // is measured from the moment the top is reached rather than from the start
  // of a long upward scroll.
  const pullAnchorY = useRef<number | null>(null);
  const wheelPull = useRef(0);

  const change = useCallback((next: boolean) => {
    settledAt.current = Date.now() + SETTLE_MS;
    pullAnchorY.current = null;
    wheelPull.current = 0;
    setCollapsed(next);
  }, []);

  const onScroll = useCallback(
    (event: UIEvent<HTMLElement>) => {
      if (!enabled) return;
      const el = event.currentTarget;
      const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
      // Clamped so iOS's rubber-band overscroll doesn't read as real movement.
      const top = Math.min(Math.max(el.scrollTop, 0), maxTop);
      const delta = top - lastTop.current;
      lastTop.current = top;
      if (Date.now() < settledAt.current) return;
      if (!collapsed && delta > 0 && top > COLLAPSE_AFTER_PX) change(true);
    },
    [enabled, collapsed, change],
  );

  const onTouchStart = useCallback((event: TouchEvent<HTMLElement>) => {
    pullAnchorY.current = event.touches[0]?.clientY ?? null;
  }, []);

  const onTouchMove = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      if (!enabled || !collapsed) return;
      const y = event.touches[0]?.clientY;
      if (y === undefined) return;
      // Still scrolling the list itself: the pull hasn't started yet.
      if (event.currentTarget.scrollTop > 0 || pullAnchorY.current === null) {
        pullAnchorY.current = y;
        return;
      }
      if (y - pullAnchorY.current > PULL_TO_REVEAL_PX) change(false);
    },
    [enabled, collapsed, change],
  );

  const onWheel = useCallback(
    (event: WheelEvent<HTMLElement>) => {
      if (!enabled || !collapsed) return;
      if (event.currentTarget.scrollTop > 0 || event.deltaY >= 0) {
        wheelPull.current = 0;
        return;
      }
      wheelPull.current -= event.deltaY;
      if (wheelPull.current > WHEEL_TO_REVEAL_PX) change(false);
    },
    [enabled, collapsed, change],
  );

  const toggle = useCallback(() => change(!collapsed), [change, collapsed]);

  return {
    collapsed: enabled && collapsed,
    toggle,
    onScroll,
    onTouchStart,
    onTouchMove,
    onWheel,
  };
}
